import {
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import { useAppTFunction, AppTFunction } from '@retail/app/i18n';
import { InputDatePicker } from '@retail/components';
import { AssortmentTag } from '@retail/my-assortment/types';
import { ItemPriority } from '@retail/products/types';
import { ContainedButton, OutlinedButton } from '@shared/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { isToday } from 'date-fns';
import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { SupplierPriorityToggleGroup } from './AddAssortmentItem';
import { TodayToggleButton } from './TodayToggleButton';

const today = new Date();

const FieldHeading = (
  props: TypographyProps<'span', { htmlFor?: string; component?: 'label' }>
) => <Typography variant="subtitle1" fontWeight={600} color="text.primary" {...props} />;

export interface ReplaceItemValues {
  replacementDate: Date;
  priority: ItemPriority;
  tagIds: number[];
}

const replaceItemSchema = (t: AppTFunction) =>
  yup.object({
    replacementDate: yup
      .date()
      .required(t('products.mgItem.fromDate.validation.required'))
      .min(today, t('products.mgItem.fromDate.validation.min')),
    priority: yup.mixed<ItemPriority>().required(),
    tagIds: yup.array().of(yup.number().required()).required(),
  });

export type ReplaceItemFunction = (values: ReplaceItemValues) => void;

interface Props {
  assortmentTags: AssortmentTag[];
  defaultTagIds?: number[];
  isSaving?: boolean;
  handleReplaceItem: ReplaceItemFunction;
  onClose: () => void;
  goBack: () => void;
}

export function ReplaceItemDialogContent({
  assortmentTags,
  defaultTagIds = [],
  isSaving,
  handleReplaceItem,
  onClose,
  goBack,
}: Props) {
  const t = useAppTFunction();

  const { control, handleSubmit, formState } = useForm<ReplaceItemValues>({
    resolver: yupResolver(replaceItemSchema(t)),
    mode: 'onSubmit',
    defaultValues: {
      priority: ItemPriority.ZERO,
      replacementDate: new Date(),
      tagIds: defaultTagIds,
    },
  });

  const replacementDateController = useController({ control, name: 'replacementDate' });
  const priorityController = useController({ control, name: 'priority' });
  const tagIdsController = useController({ control, name: 'tagIds' });

  const selectedTagIds = tagIdsController.field.value;
  const toggleTag = (tagId: number) =>
    tagIdsController.field.onChange(
      selectedTagIds.includes(tagId)
        ? selectedTagIds.filter((id) => id !== tagId)
        : [...selectedTagIds, tagId]
    );

  const replacementDate = replacementDateController.field.value;
  const isTodaySelected = !!replacementDate && isToday(replacementDate);

  const onSubmit = handleSubmit(handleReplaceItem);

  return (
    <>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {assortmentTags.length > 0 && (
          <Stack gap={1} role="group" aria-labelledby="replace-item-sub-assortments">
            <FieldHeading id="replace-item-sub-assortments">
              {t('myAssortment.columns.subAssortments')}
            </FieldHeading>
            <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2 }}>
              {assortmentTags.map((tag) => (
                <FormControlLabel
                  key={tag.id}
                  label={tag.name}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedTagIds.includes(tag.id)}
                      onChange={() => toggleTag(tag.id)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </Stack>
        )}

        <Stack gap={1}>
          <FieldHeading component="label" htmlFor={`${replacementDateController.field.name}-id`}>
            {t('myAssortment.actions.replacementDate')}
          </FieldHeading>
          <Stack flexDirection="row" gap={2} alignItems="center">
            <InputDatePicker
              {...replacementDateController.field}
              error={replacementDateController.fieldState.error?.message}
              minDate={new Date()}
              margin="none"
              size="small"
            />
            <TodayToggleButton
              selected={isTodaySelected}
              selectToday={() => replacementDateController.field.onChange(new Date())}
            />
          </Stack>
        </Stack>

        <Stack gap={1} role="group" aria-labelledby="replace-item-priority">
          <FieldHeading id="replace-item-priority">
            {t('myAssortment.status.inAssortment')}
          </FieldHeading>
          <SupplierPriorityToggleGroup
            hideLabel
            selectedPriority={priorityController.field.value}
            setPriority={(prio: ItemPriority) => priorityController.field.onChange(prio)}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <OutlinedButton size="small" onClick={onClose}>
          {t('products.cancel')}
        </OutlinedButton>
        <OutlinedButton size="small" onClick={goBack}>
          {t('common.goBack')}
        </OutlinedButton>
        <ContainedButton
          size="small"
          loading={isSaving}
          disabled={!formState.isValid}
          onClick={onSubmit}
        >
          {t('myAssortment.actions.replaceItem')}
        </ContainedButton>
      </DialogActions>
    </>
  );
}
